import React, { FC, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Button, message, Modal, Space, Table, Tag, Tooltip } from 'antd';
import { PageHeader } from '@ant-design/pro-layout';
import { WebUxPage, WebUxPageContent, WebUxPageItem } from '../../webux-layouts/WebUxPage';
import { FlexBox } from '../../../../components/atoms/FlexBox';
import {
  CaretDownOutlined,
  CaretRightOutlined,
  ExclamationCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import { DataTableColumns } from '../../../../components/molecules/DataTable/DataTable';
import { UserAvatar } from '../../../../components/atoms/UserAvatar';
import { useConnection, useEvents, useUserInfo } from '../../../../connection/Application';
import { Team } from '../../../../entity/Team';
import { FormModal, useFormModal } from '../../../../components/form-modal/FormModal';
import Form, { FormSchema } from '../../../../form/Form';
import Utils from '../../../../utils/Utils';
import { TeamView } from './TeamView';
import Spacer from '../../../../components/atoms/Spacer';
import { useHistory } from 'react-router-dom';
import { CycleToggle } from '../../../../components/atoms/ItemToggle';
import { useQueryParam } from 'use-query-params';
import { PerformanceCycle } from '../../../../entity/PerformanceCycle';

const columns = (onSelect: (team: Team) => void): DataTableColumns<Team> => [
  {
    title: 'Name',
    dataIndex: 'name',
    key: 'name',
    // eslint-disable-next-line react/display-name
    render: (value, record) => (
      <Space>
        <UserAvatar type={'team'} src={record.image} />
        <Button type={'link'} onClick={() => onSelect(record)}>
          {record.name}
        </Button>
      </Space>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'teamType',
    key: 'teamType',
    // eslint-disable-next-line react/display-name
    render: (value) => <Tag color={value?.color}>{value?.name}</Tag>,
  },
  {
    title: 'Leads',
    dataIndex: 'leads',
    key: 'leads',
    // eslint-disable-next-line react/display-name
    render: (value, record) => (
      <>
        {record.leads.map((lead) => (
          <Tooltip key={lead.id} title={`${lead.firstName} ${lead.lastName}`}>
            <UserAvatar src={lead.image} />
          </Tooltip>
        ))}
      </>
    ),
  },
  // {
  //   title: 'Members',
  //   dataIndex: 'memberCount',
  //   key: 'memberCount',
  //   align: 'right',
  //   // eslint-disable-next-line react/display-name
  //   render: (value, record) => (isNaN(record.memberCount) ? 0 : record.memberCount),
  // },
];

const updateTeamMemberCount = (team: Team): number => {
  const childMemberCount =
    team.children?.map((t) => updateTeamMemberCount(t)).reduce((a, b) => a + b) ?? 0;
  team.memberCount = team.memberCount + childMemberCount;
  return team.memberCount ?? 0;
};

const getSortOrder = (prop: string): ((a: any, b: any) => number) => {
  return (a, b) => {
    if (a[prop] > b[prop]) {
      return 1;
    } else if (a[prop] < b[prop]) {
      return -1;
    }
    return 0;
  };
};

const buildTeamsStructure = (allTeams: Team[]): Team[] => {
  const teamsCopy: Team[] = JSON.parse(JSON.stringify(allTeams));
  const teams: Team[] = [];
  const teamsMap: Record<number, Team> = Object.fromEntries(teamsCopy.map((t) => [t.id, t]));
  console.log(teamsCopy.map((e) => [e.parentId, e.name]));
  for (const t of teamsCopy.sort(getSortOrder('id'))) {
    (t as any).key = t.id;
    if (t.parentId !== undefined) {
      if (teamsMap?.[t.parentId]?.children === undefined) {
        teamsMap[t.parentId].children = [];
      }
      teamsMap[t.parentId].children.push(t);
    } else {
      teams.push(t);
    }
  }
  for (const t of teams) {
    updateTeamMemberCount(t);
  }
  return teams;
};

export const TeamsPage2: FC = () => {
  const connection = useConnection();
  const [cycle, setCycle] = useQueryParam<number | undefined>('cycle');
  const cycleRef = useRef(cycle);
  const [cycles, setCycles] = useState<PerformanceCycle[]>([]);
  const [teams, setTeams] = useState<Team[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [teamStructure, setTeamStructure] = useState<Team[]>([]);
  const [selected, setSelected] = useState<Team>();
  const [editable, setEditable] = useState<Team>();
  const [editingTeamId, setEditingTeamId] = useState<number>();
  const createForm = useFormModal<Team>();
  const history = useHistory();
  const events = useEvents();
  const user = useUserInfo();
  const [expandedKeys, setExpandedKeys] = useState<number[]>([]);

  const fetchTeams = useCallback(() => {
    if (cycles.length === 0 || !cycle) {
      return;
    }
    setLoading(true);
    const token = connection.hasSession();
    if (token) {
      setLoading(true);
      connection.get('teams', { size: 1000, cycleId: cycle }).then((value) => {
        setTeams(value.data);
        const teamStructure = buildTeamsStructure(value.data);
        console.log(teamStructure);
        setTeamStructure(teamStructure);
        setExpandedKeys(value.data.map((v: any) => v.id));
        setLoading(false);
        // keys[0]="7";
        // for (let i = 0; i < value.data.length; i++) {
        //   setKeys([... keys,{
        //     id:i,
        //     value:i
        //   }])
        // }
      });
    }
  }, [connection, cycle, cycles.length]);

  const createTeam = useCallback(
    (team: Team) => {
      if (editingTeamId) {
        connection.patch(`teams/${editingTeamId}`, team).then((value) => {
          events.emit('TEAM_UPDATED', value.team);
          const ts = Utils.mergeArrays(teams, [value.team], (t) => t.id);
          setTeams(ts);
          setTeamStructure(buildTeamsStructure(ts));
          setEditingTeamId(undefined);
          createForm.close();
        });
      } else {
        connection.post('teams', { ...team, cycleId: cycle }).then((value) => {
          const ts = Utils.mergeArrays(teams, [value.team], (t) => t.id);
          setTeams(ts);
          setTeamStructure(buildTeamsStructure(ts));
          setEditingTeamId(undefined);
          createForm.close();
        });
      }
    },
    [connection, cycle, createForm, editingTeamId, events, teams],
  );

  const deleteTeam = useCallback(
    (id: number | undefined) => {
      Modal.confirm({
        title: 'Delete Team',
        icon: <ExclamationCircleOutlined />,
        content: 'Are you sure delete to this Team?',
        okText: 'Delete',
        okType: 'danger',
        onOk() {
          connection
            .delete(`teams/${id}`)
            .then(() => {
              const ts = teams.filter((e) => e.id !== id);
              setTeams(ts);
              setTeamStructure(buildTeamsStructure(ts));
              setSelected(undefined);
              message.success('Team deleted');
            })
            .catch(() => message.error('Team Have Some Goals'));
        },
        onCancel() {
          console.log('Cancel');
        },
      });
    },
    [connection, teams],
  );

  const teamSchema: FormSchema = useMemo(() => {
    console.log(teams);
    return [
      { id: 'image', name: 'Image', type: 'avatar' },
      {
        id: 'name',
        name: 'Name',
        type: 'string',
        options: {
          rules: [{ required: true, whitespace: true, message: 'Name is required' }],
        },
      },
      {
        id: 'description',
        name: 'Description',
        type: 'text',
        options: {
          rules: [{ required: true, whitespace: true, message: 'Description is required' }],
        },
      },
      {
        id: 'teamTypeId',
        name: 'Type',
        type: 'enum',
        props: { enum: 'team-types' },
        options: {
          rules: [{ required: true, message: 'Team Type is required' }],
        },
      },
      {
        id: 'parentId',
        name: 'Parent Team',
        type: 'autocomplete',
        props: { values: teams.map((e) => ({ id: e.id, value: e.name })) },
      },
      {
        id: 'leads',
        name: 'Leads',
        type: 'autocomplete',
        props: {
          enum: 'users',
          mode: 'multiple',
          // eslint-disable-next-line react/display-name
          itemView: (value: any) => (
            <Space>
              <UserAvatar src={value.image} />
              {value.value}
            </Space>
          ),
        },
      },
      {
        id: 'members',
        name: 'Members',
        type: 'autocomplete',
        props: {
          enum: 'users',
          mode: 'multiple',
          // eslint-disable-next-line react/display-name
          itemView: (value: any) => (
            <Space>
              <UserAvatar src={value.image} />
              {value.value}
            </Space>
          ),
        },
      },
    ];
  }, [teams]);

  useEffect(() => {
    setLoading(true);
    const token = connection.hasSession();
    if (token) {
      connection
        .get<{ performanceCycle: PerformanceCycle[]; closestCycle: PerformanceCycle }>('cycles')
        .then((value) => {
          setCycles(value.performanceCycle);
          if (cycleRef.current === undefined) {
            setCycle(value.closestCycle.id, 'replace');
          }
        })
        .catch(() => setLoading(false));
    }
  }, [connection, setCycle]);

  useEffect(() => fetchTeams(), [fetchTeams]);

  const onRowClicked = useCallback((team: Team) => {
    setSelected(team);
  }, []);

  return (
    <WebUxPage>
      <PageHeader
        ghost={false}
        style={{ background: '#FFF' }}
        title="Teams"
        subTitle="Teams and Organization structure"
      />
      <WebUxPageContent>
        <WebUxPageItem>
          <FlexBox>
            <CycleToggle
              value={parseInt(`${cycle}`)}
              onChange={(value) => setCycle(value, 'replace')}
              data={cycles}
            />
          </FlexBox>
          <Space direction={'vertical'} style={{ width: '100%', marginBottom: 16 }} size={16}>
            <FlexBox horizontalAlign={'right'}>
              {(user?.roles?.includes('admin') || user?.roles?.includes('hr')) && (
                <Button
                  key="1"
                  type="primary"
                  onClick={() => {
                    setEditingTeamId(undefined);
                    createForm.open();
                  }}>
                  <PlusOutlined /> New Team
                </Button>
              )}
            </FlexBox>
          </Space>
          <Table
            size="small"
            loading={loading}
            columns={useMemo(() => columns(onRowClicked), [onRowClicked])}
            dataSource={teamStructure}
            pagination={false}
            expandable={{
              indentSize: 36,
              expandedRowKeys: expandedKeys,

              // defaultExpandAllRows: true,
              // defaultExpandedRowKeys: teamStructure.map((e) => e.id),
              // eslint-disable-next-line react/display-name
              expandIcon: ({ expanded, onExpand, record }) => (
                <>
                  {record?.children?.length > 0 &&
                    record?.children &&
                    (expanded ? (
                      <span style={{ marginRight: 10 }}>
                        <CaretDownOutlined
                          onClick={(e: any) => {
                            onExpand(record, e);
                            setExpandedKeys(expandedKeys.filter((v) => v !== record.id));
                          }}
                        />
                      </span>
                    ) : (
                      <span style={{ marginRight: 10 }}>
                        <CaretRightOutlined
                          onClick={(e: any) => {
                            onExpand(record, e);
                            setExpandedKeys([...expandedKeys, record.id]);
                          }}
                        />
                      </span>
                    ))}
                  {(record?.children?.length == 0 || !record?.children) && (
                    <span style={{ marginRight: 23 }} />
                  )}
                </>
              ),
            }}
          />
        </WebUxPageItem>
      </WebUxPageContent>
      <FormModal
        title={editingTeamId ? 'Edit Team' : 'Create Team'}
        onSubmit={createTeam}
        form={createForm}
        onClose={() => setEditingTeamId(undefined)}>
        <Form.Items schema={teamSchema} />
      </FormModal>
      <Modal
        destroyOnClose={true}
        open={!!selected}
        title={'Team'}
        onCancel={() => setSelected(undefined)}
        footer={
          <FlexBox>
            {(user?.roles?.includes('admin') || user?.roles?.includes('hr')) && (
              <>
                <Button
                  onClick={() => {
                    setEditingTeamId(selected?.id);
                    createForm.open(editable);
                    setSelected(undefined);
                  }}>
                  Edit
                </Button>
                {!teams.find((e) => e?.parentId === selected?.id) && (
                  <Button
                    danger={true}
                    onClick={() => {
                      deleteTeam(selected?.id);
                    }}>
                    Delete
                  </Button>
                )}
              </>
            )}
            <Spacer />
            {(user?.roles?.includes('admin') || user?.roles?.includes('hr')) && (
              <Button
                type="default"
                onClick={() => {
                  history.push(
                    `/webux/my-goals/goals/team/${selected?.goalHolderId}/${selected?.name.replace(
                      '/',
                      '',
                    )}/${selected?.id}?cycle=${cycle}`,
                  );
                  setSelected(undefined);
                }}>
                View Goals
              </Button>
            )}
            <Button type="primary" onClick={() => setSelected(undefined)}>
              OK
            </Button>
          </FlexBox>
        }>
        {selected && <TeamView onEditableChanged={setEditable} value={{ ...selected }} />}
      </Modal>
    </WebUxPage>
  );
};
